import { AlleleFormat, PersonDto, PersonIdentificationDto, PersonSampleDto, SampleCreateKeyboardModel } from "@app/api2/api";
import { useFormikContext } from "formik";
import { Button, Icon, Form as SForm, Divider, Table, TableHeader, TableBody, TableCell, TableHeaderCell, TableRow, FormGroup as SFormGroup } from "@/components/bp/bp-ui";
import SemanticFormikInput from "@common/Forms/SemanticFormikInput";
import SemanticFormikSelect from "@common/Forms/SemanticFormikSelect";
import { isNullEmptyObject, keyOfCheckerNested } from "@common/Utils";
import { useCollectorOptions, useCountryOptions, useIdTypeOptions, useStateOptions } from "../../Hooks/LookupOptions";
import { Link } from "react-router-dom";
import { buildUrl, AppRoutes } from "../../../AppRoutes";
import { SampleCreateKeyboardForm } from "../SampleCreateKeyboardForm";
import SampleUploadForm from "../SampleUploadForm";
import { useDialogContext } from "@app/components/React/Hooks/DialogContext";
import { SectionWithInfo } from "../components/SectionWithInfo";
import { EnrollmentFormSectionWithRepeater } from "../components/EnrollmentFormSection";
import { BPSection } from "@app/components/React/Common/BPSection";
import { BPButton } from "@/components/bp/bpbutton";


const fn = keyOfCheckerNested<PersonDto>();

export const IdentificationTab = () => {
    const { values } = useFormikContext<PersonDto>();

    return (
        <SectionWithInfo title="Identification & DNA Profiles" info="Enter any DNA profile information and official identification documents associated with the individual. This information is critical for database searching and record linking.">
            <EnrollmentFormSectionWithRepeater
                sectionKey="upload_reference_profile"
                header="Upload Reference Profile"
                maxItems={1}
                instanceType={values}
                name={"samples"}
                newItemFactory={() => ({ id: null, collectionDate: '' as any, alleleFormat: AlleleFormat.CE_RU, fileContent: '', kitId: null, sampleId: null, sampleName: '', andeSwabId: '', collectorId: '', collectorName: '' } as PersonSampleDto)}
                addButtonLabel="Add Reference Profile"
            >
                {({ editMode, item, idx }) => (
                    <SampleRow editMode={editMode} key={idx} sample={item} idx={idx}></SampleRow>

                )}
            </EnrollmentFormSectionWithRepeater>

            <EnrollmentFormSectionWithRepeater
                sectionKey="identification_documents"
                header="Identification Documents"
                instanceType={values}
                name={"identifications"}
                newItemFactory={() => ({ id: null, idTypeId: null, idCountryId: null, idStateId: null, idNumber: '' } as PersonIdentificationDto)}
                addButtonLabel="Add Identification Document">
                {({ editMode, item, idx }) => (
                    <IdentificationRow editMode={editMode} key={idx} identification={item} idx={idx} remove={() => { }} />
                )}
            </EnrollmentFormSectionWithRepeater>
        </SectionWithInfo>
    );
};

function IdentificationRow(props: { idx: number; identification: PersonIdentificationDto; remove: (arg0: any) => void; editMode: boolean; }) {
    const countryOptions = useCountryOptions({ defaultValue: props.identification.idCountryId });
    const stateOptions = useStateOptions({ defaultValue: props.identification.idStateId, countryId: props.identification.idCountryId })
    const idTypeIdOptions = useIdTypeOptions({ defaultValue: props.identification.idTypeId });

    return (<>
        <SFormGroup widths="equal">
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`identifications.${props.idx}.idTypeId`)} required label="Type" placeholder="Select Type" {...idTypeIdOptions.getDropdownProps()} />
            <SemanticFormikInput disabled={!props.editMode} name={fn(`identifications.${props.idx}.idNumber`)} required label="ID Number" maxLength={100} />
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`identifications.${props.idx}.idCountryId`)} required label="Country" placeholder="Select Country" {...countryOptions.getDropdownProps()} />
            <SemanticFormikSelect disabled={!props.editMode} name={fn(`identifications.${props.idx}.idStateId`)} clearable label="State of Issuance" placeholder="Select State/Province" {...stateOptions.getDropdownProps()} />
        </SFormGroup>
    </>);
}


function SampleRow(props: { editMode: boolean; idx: number; sample: PersonSampleDto; }) {
    // const state = useStateOptions({ countryId: props.sample.s })
    const { values, setFieldValue, setFieldTouched } = useFormikContext<PersonDto>();
    const isNewSample = !props.sample.sampleId || props.sample.sampleId <= 0;
    const dlg = useDialogContext();
    // const [isInEditMode, setInEditMode] = useState(false);

    const collectors = useCollectorOptions({ selectedId: parseInt(props.sample.collectorId) });

    return (
        <>
            <SemanticFormikInput
                disabled={!props.editMode}
                name={fn(`samples.${props.idx}.andeSwabId`)}
                placeholder="Scan or Enter swab ID (e.g., 2000000126956_PLTY)"
                description="Format: 13 digits followed by underscore and 4 letters (e.g., #############_XXXX)"
                mask="swabId"
                autoComplete="off"
                label="ANDE Rapid DNA Swab ID" maxLength={18} />

            <SFormGroup widths="equal">
                <SemanticFormikSelect
                    disabled={!props.editMode}
                    name={fn(`samples.${props.idx}.collectorId`)}
                    clearable required label="Collector" placeholder="Select Collector"
                    myAllowAdditions
                    {...collectors.getDropdownProps({
                        onChange: (event, data) => {
                            // if value matches an existing option, use that text, otherwise use the typed value
                            var val = data.options.find(o => o.value == data.value)?.text ?? data.value;

                            setFieldValue(fn(`samples.${props.idx}.collectorName`), val);
                            // setFieldValue(fn(`samples.${props.idx}.collectedBy`), val);
                        }
                    })} />
                {/* <SemanticFormikInput type="hidden" name={fn(`samples.${props.idx}.collectorName`)} required label="Collector Name" placeholder="Enter collector name" maxLength={100} /> */}
                {/* <SemanticFormikInput type="text" name={fn(`samples.${props.idx}.collectedBy`)} required label="Collected By" placeholder="Enter collector name" maxLength={100} /> */}
                <SemanticFormikInput disabled={!props.editMode} type="date" name={fn(`samples.${props.idx}.collectionDate`)} required label="Collection Date" />
            </SFormGroup>

            {isNullEmptyObject(props.sample.keyboardSample) && <>
                <BPButton type="button" icon labelPosition="left"
                    disabled={!isNullEmptyObject(props?.sample?.keyboardSample)}
                    onClick={() => {
                        dlg.show({
                            header: 'Upload Sample File',
                            content: <SampleUploadForm onParsed={async (parsed) => {
                                // console.log('parsed', parsed);
                                if (parsed && parsed.rows && parsed.rows.length > 0) {
                                    // console.log('parsed', parsed);
                                    // Populate the form fields with the parsed data
                                    // await setFieldValue(fn(`samples.${props.idx}.fileContent`), parsed.dataUrl);
                                    await setFieldValue(fn(`samples.${props.idx}.kitId`), parsed.kitId);
                                    await setFieldValue(fn(`samples.${props.idx}.sampleName`), parsed.rows[0].name);
                                    await setFieldValue(fn(`samples.${props.idx}.keyboardSample`), {
                                        kitId: parsed.kitId,
                                        name: parsed.rows[0].name,
                                        systems: parsed.rows[0].systems.map(system => ({
                                            // id: system.id,
                                            name: system.markerName,
                                            allele1: system.alleles?.[0].allele ?? '',
                                            allele2: system.alleles?.[1].allele ?? '',
                                        })),
                                    } as SampleCreateKeyboardModel); // clear any existing keyboard sample since we're using file upload now
                                    dlg.hide();
                                    // setFieldValue(fn(`samples.${props.idx}.kitId`), parsed.rows[0].kitId);
                                }
                            }} />
                        })
                    }}>
                    <Icon name="upload" />
                    Upload
                </BPButton>


                <Divider horizontal>Or</Divider>


                {/* Button opens keyboard form in modal */}
                {isNewSample && <div style={{ marginTop: 10 }}>
                    <Button
                        disabled={!isNullEmptyObject(props?.sample?.keyboardSample)}
                        icon
                        labelPosition="left"
                        onClick={() => {
                            dlg.show({
                                header: 'Manually Enter DNA ID',
                                content: <SampleCreateKeyboardForm
                                    initialValues={props.sample.keyboardSample}
                                    labId={values.labId}
                                    onSubmit={async (model) => {
                                        // console.log('keyboard form submit', model);
                                        try {
                                            if (model) {
                                                await setFieldValue(fn(`samples.${props.idx}.kitId`), model.kitId);
                                                await setFieldValue(fn(`samples.${props.idx}.sampleName`), model.name);
                                                await setFieldValue(fn(`samples.${props.idx}.keyboardSample`), model);
                                                await setFieldTouched(fn(`samples.${props.idx}.keyboardSample`), true);
                                            }
                                        } catch (e) {
                                            // swallow errors updating parent form
                                        } finally {
                                            dlg.hide();
                                        }
                                    }}
                                />
                            });
                        }}
                        type="button">
                        <Icon name="keyboard" />
                        Manually Enter DNA ID
                    </Button>
                </div>}

            </>
            }
            {/* {!isNewSample && <SForm.Field>
                <label>{'DNA ID Results File'}</label>
                <Link to={buildUrl(Routes.SampleDetails, { labId: values.labId, caseId: 0, sampleId: props.sample.sampleId })}>{props.sample.sampleName}</Link>
            </SForm.Field>} */}

            {(!isNullEmptyObject(props.sample.keyboardSample)) && <>
                {props.sample.id && <strong>{`DNA Markers for:`}<Link to={buildUrl(AppRoutes.SampleDetails, { labId: values.labId, caseId: 0, sampleId: props.sample.sampleId })}>{props.sample.sampleName}</Link></strong>}
                {!props.sample.id && <strong>{`DNA Markers for: ${props.sample.sampleName}`}</strong>}
                {/* <BPButton type="button" size="tiny" onClick={() => setInEditMode(!props.editMode)}>
                    {props.editMode ? 'Exit Edit Mode' : 'Edit Alleles'}
                </BPButton> */}
                <Divider />
                <BPSection scrolling simple>
                    <Table compact celled striped collapsing>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell collapsing>{window.translation?.common?.system ?? "System"}</TableHeaderCell>
                                <TableHeaderCell>{window.translation?.common?.allele ?? "Allele"}</TableHeaderCell>
                                <TableHeaderCell>{window.translation?.common?.allele ?? "Allele"}</TableHeaderCell>
                                {/* <TableHeaderCell /> */}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(props.sample.keyboardSample?.systems || []).map((sys, index) => (
                                <TableRow key={index}>
                                    <TableCell collapsing>
                                        {sys.name}
                                    </TableCell>
                                    <TableCell>
                                        <SemanticFormikInput
                                            isReadOnly={!props.editMode}
                                            name={fn(`samples.${props.idx}.keyboardSample.systems.${index}.allele1`)}
                                            placeholder={window.translation?.common?.allele ?? "Allele"}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SemanticFormikInput
                                            isReadOnly={!props.editMode}
                                            name={fn(`samples.${props.idx}.keyboardSample.systems.${index}.allele2`)}
                                            placeholder={window.translation?.common?.allele ?? "Allele"}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </BPSection>

            </>}
        </>
    )
}